import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableHead, TableRow, TableCell,
  TableContainer, Paper, Alert, Divider,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import SchemaPropertyRow from './SchemaPropertyRow';
import { defaultFetchLocalEntryRegistries } from './RegistryFieldPicker';
import { propertiesToRows, rowsToSchema, registrySchemaErrors } from '../utils/registrySchemaUtils';

const emptyRow = () => ({
  key: '', name: '', type: 'string', required: false, searchable: false,
  visibleOnUi: false, isKey: false, maxLength: '', pattern: '', enumText: '',
  description: '', reference: { registryId: '', field: '' },
});

/**
 * Graphical editor for a registry's schema.properties. Initialises its rows from
 * `value` on mount (give it a `key` in the parent to reset per registry), and emits
 * the full schema object via onChange on every change.
 */
const SchemaEditor = ({ value, onChange, onValidityChange, fetchRegistries = defaultFetchLocalEntryRegistries }) => {
  const baseSchemaRef = useRef(value || {});
  const idRef = useRef(0);
  const nextId = () => { idRef.current += 1; return `row-${idRef.current}`; };

  const [rows, setRows] = useState(() =>
    propertiesToRows(value).map((r) => ({ ...r, _id: nextId() })));
  const [registries, setRegistries] = useState([]);

  useEffect(() => {
    let active = true;
    Promise.resolve(fetchRegistries())
      .then((list) => { if (active) setRegistries(Array.isArray(list) ? list : []); })
      .catch(() => { if (active) setRegistries([]); });
    return () => { active = false; };
  }, [fetchRegistries]);

  const emit = (nextRows) => {
    setRows(nextRows);
    if (onChange) onChange(rowsToSchema(nextRows, baseSchemaRef.current));
  };

  const patchRow = (id, patch) =>
    emit(rows.map((r) => (r._id === id ? { ...r, ...patch } : r)));
  const removeRow = (id) => emit(rows.filter((r) => r._id !== id));
  const addRow = () => emit([...rows, { ...emptyRow(), _id: nextId() }]);

  const errors = useMemo(() => registrySchemaErrors(rows), [rows]);

  useEffect(() => { if (onValidityChange) onValidityChange(errors); }, [errors, onValidityChange]);

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle1" gutterBottom>Campi anagrafica</Typography>

      {errors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {errors.map((e, i) => (
            <div key={i}>{e.key ? `${e.key}: ` : ''}{e.message}</div>
          ))}
        </Alert>
      )}

      {rows.length > 0 ? (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                <TableCell>Chiave</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell align="center">Obbligatorio</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <SchemaPropertyRow
                  key={r._id}
                  row={r}
                  registries={registries}
                  onChange={(patch) => patchRow(r._id, patch)}
                  onRemove={() => removeRow(r._id)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography color="text.secondary" sx={{ mb: 2 }}>Nessun campo definito.</Typography>
      )}

      <Button startIcon={<AddIcon />} onClick={addRow} variant="outlined" size="small">
        Aggiungi campo
      </Button>
    </Box>
  );
};

export default SchemaEditor;
